<template>
  <div class="card mb-25 border-0 rounded-0 bg-white letter-spacing">
    <div class="card-head box-shadow bg-white d-lg-flex align-items-center justify-content-between p-15 p-sm-20 p-md-25">
      <div class="d-sm-flex align-items-center">
        <router-link v-if="isAdmin" to="/create-center"
          class="default-btn position-relative transition border-0 fw-medium text-white pt-11 pb-11 ps-25 pe-25 pt-md-12 pb-md-12 ps-md-30 pe-md-30 rounded-1 bg-success fs-md-15 fs-lg-16 d-inline-block text-decoration-none">
          {{ $t('cta.create_center') }}
          <i class="ph-bold ph-plus position-relative ms-5 fs-12"></i>
        </router-link>
      </div>
    </div>
    <div class="card-body p-15 p-sm-20 p-md-25">
      <SpinnerLoading v-if="centers === undefined" class="mt-50" />
      <div class="table-responsive" v-else>
        <table class="table text-nowrap align-middle mb-0">
          <thead>
            <tr>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                ID
              </th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                {{ $t('form.name.label') }}
              </th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                {{ $t('form.country.label') }}
              </th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                {{ $t('form.address.label') }}
              </th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                {{ $t('form.email.label') }}
              </th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                {{ $t('form.phone.label') }}
              </th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                {{ $t('form.main_contractor.label') }}
              </th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0 text-end pe-0">
              </th>
            </tr>
          </thead>
          <tbody class="linked">
            <!-- CENTER ROW -->
            <tr v-for="center in centers" :key="'tr_center_' + center.id">
              <td class="shadow-none lh-1 fw-medium text-primary" @click="show(center)">
                {{ center.id }}
              </td>
              <td class="shadow-none lh-1 fw-medium text-black-emphasis" @click="show(center)">
                {{ center.name }}
              </td>
              <td class="shadow-none lh-1 fw-medium text-body-tertiary  pe-0" @click="show(center)">
                {{ center.country }}
              </td>
              <td class="shadow-none lh-1 fw-medium text-body-tertiary  pe-0" @click="show(center)">
                {{ center.address }}
              </td>
              <td class="shadow-none lh-1 fw-medium text-body-tertiary  pe-0" @click="show(center)">
                {{ center.email }}
              </td>
              <td class="shadow-none lh-1 fw-medium text-body-tertiary  pe-0" @click="show(center)">
                {{ center.phone }}
              </td>
              <td class="shadow-none lh-1 fw-medium text-body-tertiary  pe-0" @click="show(center)">
                {{ center.main_contractor }}
              </td>
              <td class="shadow-none lh-1 fw-medium text-body-tertiary text-end">
                <div class="dropdown position-static">
                  <button class="dropdown-toggle lh-1 bg-transparent border-0 shadow-none p-0 transition" type="button"
                    data-bs-toggle="dropdown" aria-expanded="false" data-bs-boundary="viewport">
                    <i class="flaticon-dots"></i>
                  </button>
                  <ul class="dropdown-menu">
                    <li>
                      <router-link :to="{ path: `/center/${center.id}` }" class="dropdown-item d-flex align-items-center">
                        <i class="ph ph-eye lh-1 me-8 position-relative top-1"></i>
                        {{ $t('cta.view') }}
                      </router-link>
                    </li>
                    <li v-if="isAdmin">
                      <router-link :to="{ path: `/center/${center.id}/edit` }"
                        class="dropdown-item d-flex align-items-center">
                        <i class="ph-duotone ph-pencil lh-1 me-8 position-relative top-1"></i>
                        {{ $t('cta.edit') }}
                      </router-link>
                    </li>
                    <li v-if="isAdmin">
                      <button class="dropdown-item d-flex align-items-center" v-on:click="confirmDeleteCenter(center)">
                        <i class="ph ph-trash lh-1 me-8 position-relative top-1"></i>
                        {{ $t('cta.delete') }}
                      </button>
                    </li>
                  </ul>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <!-- PAGINATION -->
      <TablePagination :count="centers?.length ?? 0" :page="currentPage" :total="total" :pages="lastPage"
        @change="setPage($event)" />
    </div>
  </div>
  <ConfirmModal @confirm="deleteCenter()" :title="$t('cta.$delete.title')" :message="$t('cta.$delete.message')" />
</template>
  
<script lang="ts">
import { defineComponent } from "vue";
import ConfirmModal from "@/components/Widgets/ConfirmModal.vue";
import SpinnerLoading from "@/components/Widgets/Spinner.vue";
import { CenterInfo } from "@/utils/models/center.model";
import { Modal } from "bootstrap";
import TablePagination from "@/components/FK/TablePagination.vue";
import API from "@/services/api/api.service";
import stateStore from "@/utils/store";
import { Role } from "@/utils/models/user.model";

const PAGE_SIZE = 10;

export default defineComponent({
  name: "CentersList",
  data: () => {
    return {
      centers: undefined,
      currentPage: 1,
      lastPage: 1,
      total: 0,
      perPage: PAGE_SIZE,
      confirmModal: null,
      selectedCenter: null
    } as {
      centers?: CenterInfo[],
      currentPage: number,
      lastPage: number,
      total: number,
      perPage: number,
      confirmModal: Modal | null,
      selectedCenter: CenterInfo | null;
    }
  },
  mounted() {
    this.loadCenters(1);
    this.confirmModal = new Modal('#confirmModal', {})
  },
  computed: {
    isAdmin() { return stateStore.isAdmin() },
  },
  methods: {
    loadCenters(page: number) {
      this.centers = undefined;
      API.getCenters({page: page, zone_id: stateStore.user?.info?.zone_id}).then((res) => {
        this.centers = res.data.data;
        this.total = res.data.total;
        this.lastPage = res.data.last_page;
      })
    },
    setPage(page: number) {
      this.currentPage = page;
      this.loadCenters(page);
    },
    confirmDeleteCenter(center: CenterInfo) {
      this.selectedCenter = center;
      this.$data.confirmModal?.show();
    },
    deleteCenter(): void {
      API.deleteCenter(this.selectedCenter?.id!).then((response) => {
        if (response.data.success === true) {
          this.selectedCenter = null;
          this.setPage(1);
        }
      }).finally(() => {
        console.log("DELETED_CENTER_ID", this.selectedCenter?.id);
      })
    },
    show(center: CenterInfo) {
      this.$router.push({ path: `/center/${center.id}` });
    }
  },
  components: {
    ConfirmModal,
    SpinnerLoading,
    TablePagination
  }
});
</script>